import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Alert, Spinner, Nav, Button, Modal, Badge, Container, Dropdown } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';

const SuperAdminAccountManagement = () => {
  const [programs, setPrograms] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [unclaimedPrograms, setUnclaimedPrograms] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState({ show: false, type: null, id: null, label: '', forceAvailable: false, forceInfo: null });
  const [activeTab, setActiveTab] = useState('institutions');
  const [showCreateInstitution, setShowCreateInstitution] = useState(false);
  const [showReclaimProgram, setShowReclaimProgram] = useState(false);
  const [showEditInstitution, setShowEditInstitution] = useState(false);
  const [showAssignAdmin, setShowAssignAdmin] = useState(false);
  const [showEditAdmin, setShowEditAdmin] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [createInstitutionData, setCreateInstitutionData] = useState({
    name: '',
    type: '',
    address: '',
    website: '',
    primaryContactEmail: '',
    phoneNumber: '',
    logoUrl: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: ''
  });
  const [editInstitutionData, setEditInstitutionData] = useState({
    name: '',
    type: '',
    address: '',
    website: '',
    primaryContactEmail: '',
    phoneNumber: '',
    logoUrl: '',
    isActive: true
  });
  const [adminData, setAdminData] = useState({
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    isActive: true
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || role !== 'superuser') {
      window.location.replace('/');
      return;
    }

    const fetchAll = async () => {
      try {
        setError('');
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };
        const [pRes, dRes, iRes, uRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/super/programs`, { headers }),
          fetch(`${API_BASE_URL}/api/super/directors`, { headers }),
          fetch(`${API_BASE_URL}/api/super/institutions`, { headers }),
          fetch(`${API_BASE_URL}/api/super/programs/unclaimed`, { headers })
        ]);
        if (!pRes.ok || !dRes.ok || !iRes.ok || !uRes.ok) throw new Error('Failed to load superuser data');
        const [p, d, i, u] = await Promise.all([pRes.json(), dRes.json(), iRes.json(), uRes.json()]);
        setPrograms(p);
        setDirectors(d);
        setInstitutions(i);
        setUnclaimedPrograms(u);
      } catch (e) {
        console.error(e);
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const activeTabLabel = activeTab === 'institutions' ? 'Institutions' : activeTab === 'programs' ? 'Programs' : 'Unclaimed Programs';

  const breadcrumbItems = [
    { href: '/super/dashboard', label: 'Super Admin' },
    { href: '/super/accounts', label: 'Account Management' },
    { label: activeTabLabel }
  ];

  const refresh = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const [pRes, dRes, iRes, uRes] = await Promise.all([
      fetch(`${API_BASE_URL}/api/super/programs`, { headers }),
      fetch(`${API_BASE_URL}/api/super/directors`, { headers }),
      fetch(`${API_BASE_URL}/api/super/institutions`, { headers }),
      fetch(`${API_BASE_URL}/api/super/programs/unclaimed`, { headers })
    ]);
    const [p, d, i, u] = await Promise.all([pRes.json(), dRes.json(), iRes.json(), uRes.json()]);
    setPrograms(p); setDirectors(d); setInstitutions(i); setUnclaimedPrograms(u);
  };

  const doForceDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (confirm.type === 'program') {
        const res = await fetch(`${API_BASE_URL}/api/super/programs/${confirm.id}?force=true`, { method: 'DELETE', headers });
        if (!res.ok) throw new Error('Force delete failed');
      }
      setConfirm({ show: false, type: null, id: null, label: '', forceAvailable: false, forceInfo: null });
      await refresh();
    } catch (e) {
      setError(e.message || 'Force delete failed');
      setConfirm({ show: false, type: null, id: null, label: '', forceAvailable: false, forceInfo: null });
    }
  };

  const doDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (confirm.type === 'program') {
        const res = await fetch(`${API_BASE_URL}/api/super/programs/${confirm.id}`, { method: 'DELETE', headers });
        if (!res.ok) {
          if (res.status === 409) {
            const body = await res.json().catch(() => ({}));
            setConfirm((prev) => ({ ...prev, forceAvailable: true, forceInfo: body }));
            return;
          }
          throw new Error('Failed to delete program');
        }
      } else if (confirm.type === 'director') {
        const res = await fetch(`${API_BASE_URL}/api/super/directors/${confirm.id}`, { method: 'DELETE', headers });
        if (!res.ok) throw new Error('Failed to delete director');
      }
      setConfirm({ show: false, type: null, id: null, label: '', forceAvailable: false, forceInfo: null });
      await refresh();
    } catch (e) {
      setError(e.message || 'Delete failed');
      setConfirm({ show: false, type: null, id: null, label: '', forceAvailable: false, forceInfo: null });
    }
  };

  const handleCreateInstitution = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/super/institutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(createInstitutionData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create institution');
      }

      setShowCreateInstitution(false);
      setCreateInstitutionData({
        name: '',
        type: '',
        address: '',
        website: '',
        primaryContactEmail: '',
        phoneNumber: '',
        logoUrl: '',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminPassword: ''
      });
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReclaimProgram = async (institutionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/super/programs/${selectedProgram.id}/reclaim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ institutionId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reclaim program');
      }

      setShowReclaimProgram(false);
      setSelectedProgram(null);
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditInstitution = (institution) => {
    setSelectedInstitution(institution);
    setEditInstitutionData({
      name: institution.name || '',
      type: institution.type || '',
      address: institution.address || '',
      website: institution.website || '',
      primaryContactEmail: institution.primaryContactEmail || '',
      phoneNumber: institution.phoneNumber || '',
      logoUrl: institution.logoUrl || '',
      isActive: institution.isActive !== false
    });
    setShowEditInstitution(true);
  };

  const handleUpdateInstitution = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/super/institutions/${selectedInstitution.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editInstitutionData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update institution');
      }

      setShowEditInstitution(false);
      setSelectedInstitution(null);
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAssignAdmin = (institution) => {
    setSelectedInstitution(institution);
    setAdminData({
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
      adminPassword: '',
      isActive: true
    });
    setShowAssignAdmin(true);
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/super/institutions/${selectedInstitution.id}/assign-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(adminData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to assign admin');
      }

      setShowAssignAdmin(false);
      setSelectedInstitution(null);
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditAdmin = (institution) => {
    setSelectedInstitution(institution);
    setAdminData({
      adminFirstName: institution.adminUser?.firstName || '',
      adminLastName: institution.adminUser?.lastName || '',
      adminEmail: institution.adminUser?.email || '',
      adminPassword: '',
      isActive: institution.adminUser?.isActive !== false
    });
    setShowEditAdmin(true);
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/super/institutions/${selectedInstitution.id}/admin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(adminData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update admin');
      }

      setShowEditAdmin(false);
      setSelectedInstitution(null);
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveAdmin = async (institution) => {
    if (!window.confirm(`Are you sure you want to remove the admin account for ${institution.name}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/super/institutions/${institution.id}/admin`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove admin');
      }

      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="d-flex vh-100 overflow-hidden bg-light">
      <SuperAdminSidebar />
      {/* Main content: submenu at top + content area */}
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto ">
        <AdminTopBar variant="super" breadcrumbItems={breadcrumbItems} />
        {/* Content area */}
        <div className="flex-grow-1">
          <Container fluid className="bg-light">
            <Row>
              <Col md={2} className="border-end pt-3 adjust-min-vh bg-inverse-light">
                <Nav
                  variant="secondary-tabs"
                  className="flex-column"
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k || 'institutions')}
                >
                  <Nav.Item>
                    <Nav.Link eventKey="institutions" className="d-flex align-items-center justify-content-between">
                      Institutions <Badge bg="secondary" className="ms-1">{institutions.length}</Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="programs" className="d-flex align-items-center justify-content-between">
                      Programs <Badge bg="secondary" className="ms-1">{programs.length}</Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="unclaimed" className="d-flex align-items-center justify-content-between">
                      Unclaimed <Badge bg="warning" className="ms-1">{unclaimedPrograms.length}</Badge>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col md={10} className="p-0">


          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          {loading ? (
            <div className="d-flex align-items-center"><Spinner size="sm" className="me-2"/> Loading...</div>
          ) : (
            <>
              {activeTab === 'institutions' && (
                <Card className="border-0 rounded-0">
                  <Card.Header className="d-flex justify-content-between border-bottom-0 px-4 py-3 align-items-center">
                    <div className="d-flex flex-column">
                    <h6 className="mb-0">Institutions</h6>
                    <div className="text-muted small">{institutions.length} institutions</div>
                     
                    </div> 
                    <Button variant="primary" size="sm" onClick={() => setShowCreateInstitution(true)}>
                        Create Institution
                      </Button>
                  </Card.Header>
                  <Table hover  responsive size="sm" className="align-middle alt-table-striped mb-0">
                    <thead>
                      <tr className="custom-header-bg">
                        <th>IID</th>
                        <th className="text-start">Name</th>
                        <th>Type</th>
                        <th>Admin</th>
                        <th>Programs</th>
                        <th>Users</th>
                        <th>Status</th>
                        <th style={{width: 200}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {institutions.map(i => (
                        <tr key={i.id}>
                          <td>{i.id}</td>
                          <td className="text-start">{i.name}</td>
                          <td>{i.type || 'Not specified'}</td>
                          <td>
                            {i.adminUser ? (
                              <div>
                                <div>{i.adminUser.firstName} {i.adminUser.lastName}</div>
                                <small className="text-muted">{i.adminUser.email}</small>
                              </div>
                            ) : (
                              <span className="text-muted">No admin</span>
                            )}
                          </td>
                          <td><i className="bi bi-building-gear ms-1"></i> {i.programCount ?? '-'}</td>
                          <td><i className="bi bi-people ms-1"></i> {i.userCount ?? '-'}</td>
                          <td>
                            <Badge bg={i.isActive ? 'success' : 'danger'}>
                              {i.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="light-lite"
                                size="sm"
                                className="no-arrow"
                                id={`dropdown-institution-actions-${i.id}`}
                              >
                                <i className="bi bi-three-dots-vertical"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={() => handleEditInstitution(i)}
                                  title="Edit Institution"
                                >
                                  <i className="bi bi-pencil me-2"></i> Edit Institution
                                </Dropdown.Item>
                                {i.adminUser ? (
                                  <>
                                    <Dropdown.Item
                                      onClick={() => handleEditAdmin(i)}
                                      title="Edit Admin"
                                    >
                                      <i className="bi bi-person-gear me-2"></i> Edit Admin
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleRemoveAdmin(i)}
                                      title="Remove Admin"
                                    >
                                      <i className="bi bi-person-x me-2"></i> Remove Admin
                                    </Dropdown.Item>
                                  </>
                                ) : (
                                  <Dropdown.Item
                                    onClick={() => handleAssignAdmin(i)}
                                    title="Assign Admin"
                                  >
                                    <i className="bi bi-person-plus me-2"></i> Assign Admin
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              )}

              {activeTab === 'programs' && (
                <Card className="border-0 rounded-0">
                  <Card.Header className="d-flex justify-content-between border-bottom-0 px-4 py-3 align-items-center">
                    <div className="d-flex flex-column">
                    <h6 className="mb-0">Programs</h6>
                    <div className="text-muted small">{programs.length} programs</div>
                     
                    </div> 
                  </Card.Header>
                  <Table hover responsive size="sm" className="align-middle alt-table-striped mb-0">
                    <thead>
                      <tr className="custom-header-bg">
                        <th>PID</th>
                        <th className="text-start">Name</th>
                        <th>Specialty</th>
                        <th>Duration</th>
                        <th>Max PGY</th>
                        <th>Residents</th>
                        <th>Director</th>
                        <th>Director Email</th>
                        <th style={{width: 120}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {programs.map(p => (
                        <tr key={p.id}>
                          <td>{p.id}</td>
                          <td className="text-start">{p.name}</td>
                          <td>{p.specialty}</td>
                          <td>{p.duration}</td>
                          <td>{p.maxPGYLevel}</td>
                          <td>{p.residentCountComputed ?? p.residentCount ?? '-'}</td>
                          <td>{p.director ? `${p.director.firstName} ${p.director.lastName}` : '-'}</td>
                          <td>{p.director?.email || '-'}</td>
                          <td>
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="light-lite"
                                size="sm"
                                className="no-arrow"
                                id={`dropdown-program-actions-${p.id}`}
                              >
                                <i className="bi bi-three-dots-vertical"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={() => setConfirm({ show: true, type: 'program', id: p.id, label: `program \"${p.name}\"`, forceAvailable: false, forceInfo: null })}
                                >
                                  <i className="bi bi-trash me-2"></i> Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              )}

              {activeTab === 'unclaimed' && (
                <Card className="border-0 rounded-0 p-0 overflow-scroll adjust-min-vh">
                  <Card.Header className="d-flex justify-content-between border-bottom-0 px-4 py-3 align-items-center">
                    <div className="d-flex flex-column">
                    <h6 className="mb-0">Unclaimed Programs</h6>
                    <div className="text-muted small">Programs not yet linked to institutions</div>
                     
                    </div> 
                  </Card.Header>
                  <Table hover responsive size="sm" className="align-middle alt-table-striped mb-0">
                    <thead>
                      <tr className="custom-header-bg">
                        <th>ID</th>
                        <th className="text-start">Name</th>
                        <th>Specialty</th>
                        <th>Director</th>
                        <th>Director Email</th>
                        <th>Residents</th>
                        <th>Created</th>
                        <th style={{width: 120}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unclaimedPrograms.map(p => (
                        <tr key={p.id}>
                          <td>{p.id}</td>
                          <td>{p.name}</td>
                          <td>{p.specialty || 'Not specified'}</td>
                          <td>{p.director ? `${p.director.firstName} ${p.director.lastName}` : 'No director'}</td>
                          <td>{p.director?.email || '-'}</td>
                          <td>{p.residentCount || 0}</td>
                          <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                          <td>
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="light-lite"
                                size="sm"
                                className="no-arrow"
                                id={`dropdown-program-actions-${p.id}`}
                              >
                                <i className="bi bi-three-dots-vertical"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={() => {
                                    setSelectedProgram(p);
                                    setShowReclaimProgram(true);
                                  }}
                                >
                                  <i className="bi bi-person-plus me-2"></i> Reclaim
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              )}
            </>
          )}
          </Col>
          </Row>
        </Container>
        </div>
      </div>

      <Modal show={confirm.show} onHide={() => setConfirm({ show: false, type: null, id: null, label: '', forceAvailable: false, forceInfo: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirm.forceAvailable ? 'Delete blocked' : 'Confirm Deletion'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!confirm.forceAvailable ? (
            <>Are you sure you want to permanently delete this {confirm.type}: {confirm.label}? This action cannot be undone.</>
          ) : (
            <>
              This program cannot be deleted because it has linked users.
              {confirm.forceInfo?.userCount !== undefined && (
                <> Affected users: {confirm.forceInfo.userCount}.</>
              )}
              <br />
              You can force delete to permanently remove the program and all related data (users, schedules, evaluations, events, etc.). This action is irreversible.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!confirm.forceAvailable ? (
            <>
              <Button variant="secondary" onClick={() => setConfirm({ show: false, type: null, id: null, label: '', forceAvailable: false, forceInfo: null })}>Cancel</Button>
              <Button variant="danger" onClick={doDelete}>Delete</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setConfirm({ show: false, type: null, id: null, label: '', forceAvailable: false, forceInfo: null })}>Cancel</Button>
              <Button variant="danger" onClick={doForceDelete}>Force Delete (Permanent)</Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Create Institution Modal */}
      <Modal show={showCreateInstitution} onHide={() => setShowCreateInstitution(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Institution</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleCreateInstitution}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Institution Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={createInstitutionData.name}
                    onChange={(e) => setCreateInstitutionData({...createInstitutionData, name: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <select 
                    className="form-control" 
                    value={createInstitutionData.type}
                    onChange={(e) => setCreateInstitutionData({...createInstitutionData, type: e.target.value})}
                  >
                    <option value="">Select Type</option>
                    <option value="university">University</option>
                    <option value="hospital">Hospital</option>
                    <option value="health_system">Health System</option>
                    <option value="clinic">Clinic</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Address</label>
              <textarea 
                className="form-control" 
                value={createInstitutionData.address}
                onChange={(e) => setCreateInstitutionData({...createInstitutionData, address: e.target.value})}
              />
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Website</label>
                  <input 
                    type="url" 
                    className="form-control" 
                    value={createInstitutionData.website}
                    onChange={(e) => setCreateInstitutionData({...createInstitutionData, website: e.target.value})}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input 
                    type="tel" 
                    className="form-control" 
                    value={createInstitutionData.phoneNumber}
                    onChange={(e) => setCreateInstitutionData({...createInstitutionData, phoneNumber: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Primary Contact Email</label>
              <input 
                type="email" 
                className="form-control" 
                value={createInstitutionData.primaryContactEmail}
                onChange={(e) => setCreateInstitutionData({...createInstitutionData, primaryContactEmail: e.target.value})}
              />
            </div>
            <hr />
            <h6>Institutional Admin Details</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin First Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={createInstitutionData.adminFirstName}
                    onChange={(e) => setCreateInstitutionData({...createInstitutionData, adminFirstName: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin Last Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={createInstitutionData.adminLastName}
                    onChange={(e) => setCreateInstitutionData({...createInstitutionData, adminLastName: e.target.value})}
                    required 
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin Email *</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={createInstitutionData.adminEmail}
                    onChange={(e) => setCreateInstitutionData({...createInstitutionData, adminEmail: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin Password *</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    value={createInstitutionData.adminPassword}
                    onChange={(e) => setCreateInstitutionData({...createInstitutionData, adminPassword: e.target.value})}
                    required 
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateInstitution(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Institution
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Reclaim Program Modal */}
      <Modal show={showReclaimProgram} onHide={() => setShowReclaimProgram(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reclaim Program</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProgram && (
            <div>
              <p><strong>Program:</strong> {selectedProgram.name}</p>
              <p><strong>Director:</strong> {selectedProgram.director ? `${selectedProgram.director.firstName} ${selectedProgram.director.lastName}` : 'No director'}</p>
              <p><strong>Email:</strong> {selectedProgram.director?.email || 'Not available'}</p>
              <hr />
              <p>Select an institution to reclaim this program under:</p>
              <div className="list-group">
                {institutions.map(inst => (
                  <button
                    key={inst.id}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => handleReclaimProgram(inst.id)}
                  >
                    <div className="d-flex w-100 justify-content-between">
                      <h6 className="mb-1">{inst.name}</h6>
                      <small>{inst.type || 'Not specified'}</small>
                    </div>
                    <p className="mb-1">{inst.programCount || 0} programs</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReclaimProgram(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Institution Modal */}
      <Modal show={showEditInstitution} onHide={() => setShowEditInstitution(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Institution</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleUpdateInstitution}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Institution Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={editInstitutionData.name}
                    onChange={(e) => setEditInstitutionData({...editInstitutionData, name: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <select 
                    className="form-control" 
                    value={editInstitutionData.type}
                    onChange={(e) => setEditInstitutionData({...editInstitutionData, type: e.target.value})}
                  >
                    <option value="">Select Type</option>
                    <option value="university">University</option>
                    <option value="hospital">Hospital</option>
                    <option value="health_system">Health System</option>
                    <option value="clinic">Clinic</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Address</label>
              <textarea 
                className="form-control" 
                value={editInstitutionData.address}
                onChange={(e) => setEditInstitutionData({...editInstitutionData, address: e.target.value})}
              />
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Website</label>
                  <input 
                    type="url" 
                    className="form-control" 
                    value={editInstitutionData.website}
                    onChange={(e) => setEditInstitutionData({...editInstitutionData, website: e.target.value})}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input 
                    type="tel" 
                    className="form-control" 
                    value={editInstitutionData.phoneNumber}
                    onChange={(e) => setEditInstitutionData({...editInstitutionData, phoneNumber: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Primary Contact Email</label>
              <input 
                type="email" 
                className="form-control" 
                value={editInstitutionData.primaryContactEmail}
                onChange={(e) => setEditInstitutionData({...editInstitutionData, primaryContactEmail: e.target.value})}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Logo URL</label>
              <input 
                type="url" 
                className="form-control" 
                value={editInstitutionData.logoUrl}
                onChange={(e) => setEditInstitutionData({...editInstitutionData, logoUrl: e.target.value})}
              />
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input 
                  type="checkbox" 
                  className="form-check-input" 
                  id="isActive"
                  checked={editInstitutionData.isActive}
                  onChange={(e) => setEditInstitutionData({...editInstitutionData, isActive: e.target.checked})}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Institution is active
                </label>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditInstitution(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Institution
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Assign Admin Modal */}
      <Modal show={showAssignAdmin} onHide={() => setShowAssignAdmin(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Admin to {selectedInstitution?.name}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleCreateAdmin}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin First Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={adminData.adminFirstName}
                    onChange={(e) => setAdminData({...adminData, adminFirstName: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin Last Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={adminData.adminLastName}
                    onChange={(e) => setAdminData({...adminData, adminLastName: e.target.value})}
                    required 
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin Email *</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={adminData.adminEmail}
                    onChange={(e) => setAdminData({...adminData, adminEmail: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin Password *</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    value={adminData.adminPassword}
                    onChange={(e) => setAdminData({...adminData, adminPassword: e.target.value})}
                    required 
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAssignAdmin(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Assign Admin
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Edit Admin Modal */}
      <Modal show={showEditAdmin} onHide={() => setShowEditAdmin(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Admin for {selectedInstitution?.name}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleUpdateAdmin}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin First Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={adminData.adminFirstName}
                    onChange={(e) => setAdminData({...adminData, adminFirstName: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin Last Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={adminData.adminLastName}
                    onChange={(e) => setAdminData({...adminData, adminLastName: e.target.value})}
                    required 
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Admin Email *</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={adminData.adminEmail}
                    onChange={(e) => setAdminData({...adminData, adminEmail: e.target.value})}
                    required 
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">New Password (leave blank to keep current)</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    value={adminData.adminPassword}
                    onChange={(e) => setAdminData({...adminData, adminPassword: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-check">
                <input 
                  type="checkbox" 
                  className="form-check-input" 
                  id="adminIsActive"
                  checked={adminData.isActive}
                  onChange={(e) => setAdminData({...adminData, isActive: e.target.checked})}
                />
                <label className="form-check-label" htmlFor="adminIsActive">
                  Admin account is active
                </label>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditAdmin(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Admin
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default SuperAdminAccountManagement;


